import {useCallback, useMemo, type FC} from 'react'
import type {Organization, Enterprise} from '@github-ui/repos-types'
import type {Repository} from '@github-ui/current-repository'
import {UserFilterProvider} from './providers/user'
import {RuleStatusFilterProvider} from './providers/rule-status'
import {ShaFilterProvider} from './providers/sha'
import {
  SecurityProductsFilterBar,
  parseAsUser,
  serializeLogin,
  filterDefinition,
  createdFilterDefinition,
  timePeriodFilterDefinition,
  evaluateStatusFilterDefinition,
  repositoryFilterDefinition,
  staticOrganizationFilterDefinition,
  rulesetFilterDefinition,
  type FilterDefinition,
  timeoutsFilterDefinition,
} from './SecurityProductsFilterBar'
import type {
  InsightsFilter,
  InsightsFilterKey,
  RuleStatus,
  Ruleset,
  SourceType,
} from '@github-ui/repos-rules-types/rules-types'
import {insightsIndexPath} from '../../helpers/insights-filter'
import {useInsightsActors} from '../../hooks/use-insights-actors'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {useBranchFilterDefinitions} from '../../hooks/use-branch-filter-definitions'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useIsStafftools} from '../../hooks/use-is-stafftools'

type InsightsFilterBar2Props = {
  branchIsValid?: boolean
  filter: InsightsFilter
  source: Repository | Organization | Enterprise
  sourceType: SourceType
  organizations?: string[]
  rulesets: Ruleset[]
  supportedFilters?: InsightsFilterKey[]
  supportedRuleStatuses?: RuleStatus[]
}

export const InsightsFilterBar2: FC<InsightsFilterBar2Props> = ({
  branchIsValid,
  filter,
  source,
  sourceType,
  organizations,
  rulesets,
  supportedFilters,
  supportedRuleStatuses,
}) => {
  const repo = source as Repository
  const {definitions: branchDefinitions} = useBranchFilterDefinitions<InsightsFilter>({
    sourceType,
    ownerLogin: repo.ownerLogin,
    repoName: repo.name,
    branchIsValid,
    filterBranch: filter.branch,
  })
  const rulesInsightsFilterBarCreated = isFeatureEnabled('rules_insights_filter_bar_created')
  const showShaFilter = sourceType === 'repository'
  const supports = useCallback((key: InsightsFilterKey) => supportedFilters?.includes(key) ?? true, [supportedFilters])
  // 'all' only clears the filter, so fewer than two real statuses leaves nothing to choose between.
  const selectableRuleStatuses = supportedRuleStatuses?.filter(status => status !== 'all')
  const showRuleStatusFilter = !selectableRuleStatuses || selectableRuleStatuses.length > 1
  const {users: actors} = useInsightsActors()
  const {resolvePath} = useRelativeNavigation()
  const repoSuggestionsUrl = resolvePath('repo_suggestions')
  const isStafftools = useIsStafftools()

  const filterDefinitions = useMemo<Array<FilterDefinition<InsightsFilter>>>(
    () => [
      ...(rulesInsightsFilterBarCreated
        ? supports('created')
          ? [createdFilterDefinition<InsightsFilter>()]
          : []
        : supports('time_period')
          ? [timePeriodFilterDefinition<InsightsFilter>()]
          : []),
      ...(supports('evaluate_status') ? [evaluateStatusFilterDefinition<InsightsFilter>()] : []),
      ...(isStafftools ? [timeoutsFilterDefinition<InsightsFilter>()] : []),
      ...branchDefinitions,
      ...(sourceType === 'organization' && supports('repository')
        ? [repositoryFilterDefinition<InsightsFilter>(repoSuggestionsUrl)]
        : []),
      ...(sourceType === 'enterprise' && supports('organization') && organizations?.length
        ? [staticOrganizationFilterDefinition<InsightsFilter>(organizations)]
        : []),
      ...(supports('ruleset') ? [rulesetFilterDefinition<InsightsFilter>(rulesets)] : []),
      ...(supports('actor')
        ? [
            filterDefinition<InsightsFilter>({
              config: {
                filterKey: 'actor',
                queryKey: 'user',
                parse: parseAsUser,
                serialize: (v: unknown) => serializeLogin(v as {login: string}),
              },
              provider: new UserFilterProvider(actors),
            }),
          ]
        : []),
      ...(showRuleStatusFilter
        ? [
            filterDefinition<InsightsFilter>({
              config: {filterKey: 'ruleStatus', queryKey: 'rule-status'},
              provider: new RuleStatusFilterProvider(supportedRuleStatuses),
            }),
          ]
        : []),
      ...(showShaFilter
        ? [
            filterDefinition<InsightsFilter>({
              config: {filterKey: 'afterOid', queryKey: 'sha'},
              provider: new ShaFilterProvider(),
            }),
          ]
        : []),
    ],
    [
      rulesInsightsFilterBarCreated,
      isStafftools,
      branchDefinitions,
      sourceType,
      supports,
      showRuleStatusFilter,
      supportedRuleStatuses,
      repoSuggestionsUrl,
      organizations,
      rulesets,
      actors,
      showShaFilter,
    ],
  )

  return (
    <SecurityProductsFilterBar<InsightsFilter>
      id="rules-insights-filter"
      label="Filter rules insights"
      filter={filter}
      filterDefinitions={filterDefinitions}
      getIndexPath={insightsIndexPath}
    />
  )
}
